/**
 * apply-drafts.ts — Phase 049 Plan 02 Task 4 (DRAFT-02 projector).
 *
 * Pure `applyDrafts(loaded, drafts) → LoadedApp` transformer that
 * re-derives the `ui` tree per dirty block by deep-merging
 * `{...env.properties, ...drafts[env.id]}` and re-running
 * `synthesizeUi`. Clean blocks pass through as IDENTICAL references so
 * AppV2's sub-tree memoization stays effective on multi-block apps
 * (RESEARCH §"Pattern 2" + Pitfall 3 reference-identity).
 *
 * Why per-dirty-block `synthesizeUi` re-run is the ONLY correct
 * projection (CONTEXT D-049-02 step 2 + RESEARCH "Anti-Patterns" first
 * bullet): deep-merging `properties` on the OLD `ui` tree is structurally
 * wrong — list-typed properties like `iterate_over` change the rendered
 * children count (a list of 5 rows after `iterate_over: tasks → orders`
 * with 12 orders has 12 rows, not 5). `synthesizeUi` is the function
 * that materializes the row count from the data source path; it MUST
 * re-fire on every property change. (For text/heading/input blocks the
 * children count is constant, but the cost is amortized — the projector
 * still re-runs per dirty block, never per-property.)
 *
 * Fast-path identity (RESEARCH Pitfall 8): when `drafts` is empty,
 * `applyDrafts` returns the input `loaded` reference verbatim
 * (`===`-identical). This kills the per-keystroke clone storm in the
 * consumer's `cloned` memo — the `useProjectedApp` hook's `useMemo` dep
 * on the returned value stays stable when there are no drafts, so
 * `cloned`'s `JSON.parse(JSON.stringify(...))` work never re-fires.
 *
 * `tapControlIds` caching (RESEARCH OQ#4): in Phase 049 flows are NOT
 * editable (out-of-scope until v2 FLOW-01), so the input-owner block's
 * `tapControlIds` derivation is constant across baseline and projected
 * renders for any single `loaded` reference. The helper memoizes the
 * derivation via a `WeakMap<LoadedApp, ...>` keyed on the baseline app
 * reference so per-keystroke projection invocations don't re-walk the
 * blocks for the input owner.
 *
 * Pure function — no React, no Zustand reads inside. The caller
 * (`use-projected-app.ts`) is the React seam.
 */

import {
  synthesizeUi,
  readEnvelope,
  type AboxEnvelope,
} from '@/features/design-system/ontology/abox-render-prep'
import type {
  LoadedApp,
  BlockDef,
  PreviewSelectionRef,
  Flow,
} from '@/features/page-editor/store/types'

/**
 * Per-baseline `tapControlIds` cache (OQ#4). Keyed on the baseline
 * `LoadedApp` reference — when the loader produces a new `LoadedApp`
 * (project switch / re-load) the WeakMap entry is GC'd automatically.
 */
const tapCache = new WeakMap<
  LoadedApp,
  { ownerName: string | null; tapControlIds: readonly string[] }
>()

/**
 * The persisted flow trigger envelope `toFlow` stashes at
 * `flow._aboxTrigger` ({ event, target }). Minimal copy of the shape;
 * matches `abox-render-prep.ts:336-339`.
 */
interface AboxTrigger {
  event: string
  target: string
}

function readTrigger(flow: unknown): AboxTrigger | null {
  if (!flow || typeof flow !== 'object') return null
  const trig = (flow as Record<string, unknown>)._aboxTrigger
  if (!trig || typeof trig !== 'object') return null
  const { event, target } = trig as Record<string, unknown>
  if (typeof event !== 'string' || typeof target !== 'string') return null
  if (!event || !target) return null
  return { event, target }
}

/**
 * Resolve the input-owner block's `tapControlIds` from the BASELINE
 * loaded app. Mirrors `prepareAboxAppForRender:388-410` (the minimal
 * subset — block-type check + owned-flow scan). The result is cached
 * per-baseline so per-keystroke projector invocations don't re-walk.
 *
 * RESEARCH OQ#4: in Phase 049 flows are NOT editable, so this value
 * is constant for the lifetime of the baseline `LoadedApp` reference.
 */
function resolveTapControlIds(loaded: LoadedApp): {
  ownerName: string | null
  tapControlIds: readonly string[]
} {
  const cached = tapCache.get(loaded)
  if (cached) return cached

  let ownerName: string | null = null
  for (const [name, block] of Object.entries(loaded.blocks)) {
    if (readEnvelope(block)?.block_type === 'input') {
      ownerName = name
      break
    }
  }

  const tapControlIds: string[] = []
  if (ownerName) {
    for (const flow of Object.values(loaded.flows ?? {})) {
      const trigger = readTrigger(flow)
      if (trigger && trigger.event === 'tap') {
        tapControlIds.push(trigger.target)
      }
    }
  }

  const value = { ownerName, tapControlIds: tapControlIds as readonly string[] }
  tapCache.set(loaded, value)
  return value
}

/**
 * Pure projector — re-derive the `LoadedApp` ui tree per dirty block by
 * deep-merging drafts onto `__aboxEnvelope.properties` and re-running
 * `synthesizeUi`. Clean blocks pass through as IDENTICAL references so
 * AppV2 sub-tree memo stays effective.
 *
 * @param loaded  the baseline `LoadedApp` from
 *                `useStudioStore.getState().currentProject`
 * @param drafts  `Record<blockId, Record<propertyKey, value>>` from
 *                `useDraftStore.getState().drafts`
 * @returns       a NEW `LoadedApp` when any draft exists, OR the input
 *                `loaded` verbatim when drafts are empty (Pitfall 8
 *                fast path — `===` identical to the input)
 */
export function applyDrafts(
  loaded: LoadedApp,
  drafts: Record<string, Record<string, unknown>>,
): LoadedApp {
  // RESEARCH Pitfall 8 fast path — drafts empty → return loaded verbatim
  // (`===` identical). The consumer's `cloned` memo's `[source]` dep is
  // stable in this case → no per-keystroke clone storm.
  if (!loaded.blocks || Object.keys(drafts).length === 0) return loaded

  const { ownerName, tapControlIds } = resolveTapControlIds(loaded)
  const blocks: Record<string, BlockDef> = {}
  // RESEARCH Pattern 2 — the projector re-mints `aboxElementMap` entries
  // ONLY for dirty blocks (clean blocks' entries stay valid because the
  // synthesizer emits the same ids for the same block_type + block_name).
  // The final return merges baseline + dirty entries.
  const dirtyElementMap: Record<string, PreviewSelectionRef> = {}

  for (const [name, block] of Object.entries(loaded.blocks)) {
    const env: AboxEnvelope | null = readEnvelope(block)
    if (!env) {
      // Defensive — non-ABox block (no `__aboxEnvelope` stash). The hook
      // gates on `loaded.aboxElementMap != null` so this branch is
      // structurally unreachable on the ABox path, but stay safe.
      // IDENTICAL REF — preserves AppV2 sub-tree memo (Pitfall 3).
      blocks[name] = block
      continue
    }
    const blockDrafts = drafts[env.id]
    if (!blockDrafts || Object.keys(blockDrafts).length === 0) {
      // Clean block — IDENTICAL REF (Pitfall 3).
      blocks[name] = block
      continue
    }
    // Dirty block — deep-merge drafts onto baseline `__aboxEnvelope.properties`
    // and re-run `synthesizeUi`. CONTEXT D-049-02 step 2 — per-dirty-block
    // re-run is the ONLY correct projection (deep-merge alone is wrong
    // because `iterate_over` changes children count).
    // prettier-ignore — single-line spread matches PATTERNS.md verbatim.
    const merged: Record<string, unknown> = { ...env.properties, ...blockDrafts }
    // OQ#4 — tap-flow ownership check is per-block, not per-property; the
    // tapControlIds derivation cached above is reused verbatim.
    const ownsFlows = name === ownerName && tapControlIds.length > 0
    const ui = synthesizeUi(
      env.block_type,
      env.block_name || name,
      merged,
      ownsFlows ? tapControlIds : undefined,
      dirtyElementMap,
      env.id,
    )
    // Emit a new block with the re-derived `ui` AND the merged envelope
    // stashed at `data.__aboxEnvelope` so Phase 051's Save controller can
    // read the projected values directly off the block.
    // Inline envelope re-emit so the projected block carries the merged
    // properties as the canonical `data.__aboxEnvelope` stash — Phase 051's
    // Save controller reads drafts off the projected block, NOT the
    // baseline. Single-line spread mirrors PATTERNS.md verbatim.
    // prettier-ignore
    const nextData = { ...(block.data as object), __aboxEnvelope: { ...env, properties: merged } } as unknown as BlockDef['data']
    blocks[name] = { ...block, ui, data: nextData } as BlockDef
  }

  // Merge baseline + dirty `aboxElementMap` entries. Baseline entries for
  // clean blocks (block_name + block_type unchanged) stay valid because
  // `synthesizeUi` emits deterministic ids — the baseline entry MAP key is
  // identical to what we would re-mint. Dirty entries overwrite (the dirty
  // pass MAY emit a different propertyKey association if the value's
  // structural role changed, though for the v2.8 surface that's a no-op).
  return {
    ...loaded,
    blocks,
    aboxElementMap: { ...loaded.aboxElementMap, ...dirtyElementMap },
  }
}

// Type re-exports for downstream consumers that need the same `Flow`
// type symbol while reading flow envelopes off the loaded app.
export type { Flow }
